import {Fragment, ReactNode, useMemo, useRef, useState} from 'react'
import {ComboboxInput, ComboboxPopover} from '@/components/ui/combobox-atoms/ComboboxAtoms'
import {SearchSmIcon} from '@/components/ui/icon/Icon'
import {SelectComboboxList} from '@/components/ui/select-atoms/SelectAtoms'
import {AgendaSidebarSearchEquipeList} from '@/features/agenda/components/agenda-sidebar-search/AgendaSidebarSearchEquipeList'
import {AgendaSidebarSearchPatientsList} from '@/features/agenda/components/agenda-sidebar-search/AgendaSidebarSearchPatientsList'
import {AgendaSidebarSearchUsersList} from '@/features/agenda/components/agenda-sidebar-search/AgendaSidebarSearchUsersLists'
import {ComboboxProvider, useComboboxStore} from '@ariakit/react'
import {useTranslation} from '@/translations/i18n'
import {useInputSearch} from '@/hooks/useInputSearch'
import {useAgendaSearchHandlers} from '@/features/agenda/hooks/useAgendaSearchHandlers'
import {SearchTarget} from '@/features/agenda/types'
import {useAgendaNavigation} from '@/features/agenda/hooks/useAgendaNavigation'
import {useAgendaSearchParams} from '@/features/agenda/hooks/useAgendaSearchParams'

export const AgendaSidebarSearch = ({searchTargets}: {searchTargets: SearchTarget[]}) => {
    const {t} = useTranslation()
    const searchInputRef = useRef<HTMLInputElement>(null)
    const searchTargetListRef = useRef<HTMLDivElement>(null)

    const {
        searchParams: {category, subjectIds, patientId},
        clearSearchParams
    } = useAgendaSearchParams()

    // Get handlers for different selection types
    const {handleTeamSelection, handleUserSelection, handlePatientSelection} = useAgendaSearchHandlers()

    // Set the search value when the user interacts with the search input
    const {searchValue, onSearch, onResetSearch} = useInputSearch()

    // Get the entity from the URL params
    const {
        params: {entity}
    } = useAgendaNavigation()

    const isPatientViewLocked = useMemo(() => {
        return (!subjectIds || subjectIds.length == 0) && entity == 'patients'
    }, [subjectIds, entity])

    // Set the query enabled state
    const [isQueryEnabled, setIsQueryEnabled] = useState(isPatientViewLocked)

    // Handle selection of an item
    const handleSelectedValueChange = (values: string[]) => {
        const value =
            category != 'team'
                ? values.filter(v => !subjectIds?.includes(v))?.[0]
                : values.filter(v => v != patientId)?.[0] || null

        if (typeof value != 'string') return

        // Get the source of the selected item
        const comboboxList = document.getElementById('search-target-list')
        const comboboxItem = comboboxList?.querySelector(`[data-value="${value}"]`)
        const sourceComboboxItem = comboboxItem?.getAttribute('data-source')

        switch (sourceComboboxItem) {
            case 'team':
                handleTeamSelection(value)
                break
            case 'users':
                handleUserSelection(value)
                break
            case 'patients':
                handlePatientSelection(value)
                break
            default:
                clearSearchParams()
                return null
        }

        combobox.setOpen(false)
    }

    // Render the search targets
    const searchTargetRenderer = {
        team: <AgendaSidebarSearchEquipeList isQueryEnabled={isQueryEnabled} search={searchValue} />,
        users: <AgendaSidebarSearchUsersList isQueryEnabled={isQueryEnabled} search={searchValue} />,
        patients: (
            <AgendaSidebarSearchPatientsList
                isQueryEnabled={isQueryEnabled || isPatientViewLocked}
                search={searchValue}
            />
        )
    } as const satisfies Record<SearchTarget, ReactNode>

    // Create the combobox store
    const combobox = useComboboxStore({
        placement: 'right-start',
        open: isPatientViewLocked ? true : undefined,
        setOpen: open => setIsQueryEnabled(open),
        setValue: onSearch,
        resetValueOnHide: true,
        selectedValue: category == 'team' ? (patientId ? [patientId] : []) : subjectIds ?? [],
        setSelectedValue: handleSelectedValueChange
    })

    return (
        <>
            <ComboboxProvider store={combobox}>
                <ComboboxInput
                    ref={searchInputRef}
                    hasCloseCombobox
                    placeholder={
                        searchTargets.includes('patients')
                            ? t('commons:search_patient')
                            : t('agenda:search_healthcare_professional')
                    }
                    typeIcon={<SearchSmIcon size={16} />}
                    onClear={() => onResetSearch(searchInputRef)}
                />
                <ComboboxPopover portal>
                    <SelectComboboxList>
                        <div id="search-target-list" ref={searchTargetListRef}>
                            {searchTargets.map(target => (
                                <Fragment key={target}>{searchTargetRenderer[target]}</Fragment>
                            ))}
                        </div>
                    </SelectComboboxList>
                </ComboboxPopover>
            </ComboboxProvider>
        </>
    )
}
